import { ElementType, ToolboxItemType } from 'src/app/_core/models/ToolboxModel';
import { Marquise } from './marquise.model';
import { v4 as uuidv4 } from 'uuid';
import { IMarquiseInfo } from '../../interfaces/IMarquiseInfo.interface';
import { Color } from '../colors/color.model';
import { BarProfile } from '../profiles/bar.model';
import { SvgParams } from '../../constants/constants';
import { ILine } from '../../interfaces/ILine.interface';
import { Glass } from '../glasses/glass.model';
import { ElementSide } from '../../interfaces/IElementSide';
import { Common } from '../common';
import { LayerLevel } from '../../interfaces/LayerLevel';
import { RearParameter } from '../wizard/wizard-parameter.model';
import { IMarquiseHandler } from '../../interfaces/IMarquiseHandler.interface';
import { ProjectService } from '../../services/project.service';
import { IBomInfo } from '../../interfaces/IElement';

export class MarquiseTop extends Marquise {

	private _markers: ILine[] = [];
	private _corrBack: number;
	private _insertionLength: number;

	public maxDepth: number;

	public get correctionBack(): number {
		return this._corrBack;
	}

	public get channels(): number {
		return this.marquiseInfo.channels;
	}

	public get layer(): LayerLevel {
		return this.type == ElementType.MarquiseTopUp ? LayerLevel.Outside : LayerLevel.Inside;
	}

	public get insertionLength() {
		return this._insertionLength;
	}

	public get optionName(): string {
		return this.marquiseInfo.name;
	}

	public isExpandedBack = null;

	getBomInfo(): IBomInfo {
		return {
			height: null,
			width: this.getWidth(),
			depth: this.uiDepth
		}
	}

	protected calculateUIDepth(): number {
		var erpd = this.erpDepth;
		if (this.type == ElementType.MarquiseTopUp) {
			erpd -= this.marquiseInfo.depthCorrection;
		} else {
			erpd -= this.marquiseTopBottomCorrection + (this.isExpandedBack ? this.correctionBack / SvgParams.SCALE : 0);
		}

		erpd = Common.calculateFlat(erpd, this.drop);
		return erpd;
	}

	protected calculateERPDepth(): number {
		var depth = Common.calculateDepth(this.uiDepth, this.drop);

		if (this.type == ElementType.MarquiseTopUp) {
			depth += this.marquiseInfo.depthCorrection;
		} else {
			depth += this.marquiseTopBottomCorrection + (this.isExpandedBack ? this.correctionBack / SvgParams.SCALE : 0);
		}
		return Math.round(depth);
	}

	public get firstGlass(): Glass {
		return this.leftBar.rightGlass;
	}

	public get lastGlass(): Glass {
		return this.rightBar.leftGlass;
	}

	public getMarquiseOnSide(side: ElementSide, elements: any[]) {
		const sg = side == ElementSide.Left ? 'firstGlass' : 'lastGlass';
		const fl = side == ElementSide.Left ? 'isFirst' : 'isLast';
		if (this[sg][fl]) {
			return null;
		}
		var m: MarquiseTop =
			side == ElementSide.Left ?
		 		elements[this.type].find(m => this.leftBar.id == m.rightBar.id)
				:
				elements[this.type].find(m => this.rightBar.id == m.leftBar.id);

		return m;
	}

	private getAvalilableHandlers(neib: boolean, projectService: ProjectService) {
		var handlers: IMarquiseHandler[] = [];	
		const currCorr = this.leftHandler?.widthCorrection ?? 0;
		const currWidth = this.getWidth();
		const maxWidth = this.marquiseInfo.maxWidth;
		var roofWindows = projectService.profileService.roofElements[ElementType.RoofWindow];
		var isBottom = this.marquiseInfo.type == ToolboxItemType.MarquiseTopBottom;
		this.marquiseInfo.handlers.filter(d => d.double == neib).forEach(h => {
			if (currWidth + currCorr - h.widthCorrection <= maxWidth &&
				(isBottom || h.roofWindow == (roofWindows.length > 0))) {
				handlers.push(h);
			}
		});

		return handlers;
	}

	public getAvalilableLeftHandlers(projectService: ProjectService) {
		const neib = this.getMarquiseOnSide(ElementSide.Left, projectService.profileService.roofElements) != null;
		return this.getAvalilableHandlers(neib, projectService);
	}

	public getAvalilableRightHandlers(projectService: ProjectService) {
		const neib = this.getMarquiseOnSide(ElementSide.Right, projectService.profileService.roofElements) != null;
		return this.getAvalilableHandlers(neib, projectService);
	}

	constructor(
		rear: RearParameter,
		depth: number,
		currentMarquiseInfo: IMarquiseInfo,
		standalone: boolean,
		drop: number,
		type: ElementType,
		public leftBar: BarProfile,
		public rightBar: BarProfile,
		leftHandler: IMarquiseHandler,
		rightHandler: IMarquiseHandler
	) {
		super(currentMarquiseInfo.id);
		this.id = type + "_" + uuidv4();

		this.leftHandler = leftHandler;
		this.rightHandler = rightHandler;
		this.paletteId = currentMarquiseInfo.paletteId;
		this.maxDepth = currentMarquiseInfo.maxDepth;
		(this.fabric as Color | {}) = null;
		this.marquiseInfo = currentMarquiseInfo;
		this.marquiseTopBottomCorrection = rear.marquiseTopBottomCorrection;
		this._insertionLength = rear.marquiseInsertionLength;
		this.drop = drop;

		switch(type) {
			case ElementType.MarquiseTopUp:
				this._corrBack = 0;
				break;
			case ElementType.MarquiseTopBottom:
				this._corrBack = standalone ? 0 : Common.round(SvgParams.SCALE * this._insertionLength);
				break;
		}

		this.engine.setDefaultEngineParams(currentMarquiseInfo);

		this.setUIDepth(depth);
		this.setCover(depth);
		this.setExtension(0);

	}

	private calcWidth(handler: IMarquiseHandler) {
		var v = this.getRawWidth() - handler.widthCorrection * 2;
		if (this.leftBar.type == ElementType.SideFinish) {
			v -= 30;
		}
		if (this.rightBar.type == ElementType.SideFinish) {
			v -= 30;
		}

		return v;
	}

	public calculateHandler(double: boolean, roofWindow: boolean) {
		var handlers: IMarquiseHandler[] = [];
		if (this.type == ElementType.MarquiseTopUp) {
			handlers = this.marquiseInfo.handlers.filter(h => h.double == double && h.roofWindow == roofWindow);
		} else {
			handlers = this.marquiseInfo.handlers.filter(h => h.double == double); // bottom marquise ignores roof window
		}

		handlers.every(h => {
			if (this.calcWidth(h) <= this.marquiseInfo.maxWidth) {
				this.leftHandler = h;
				this.rightHandler = h;
				return false;
			}
		})
	}

	public calcHandler(double: boolean, roofWindow: boolean) {
		var handlers: IMarquiseHandler[] = [];
		if (this.type == ElementType.MarquiseTopUp) {
			handlers = this.marquiseInfo.handlers.filter(h => h.double == double && h.roofWindow == roofWindow);
		} else {
			handlers = this.marquiseInfo.handlers.filter(h => h.double == double); // bottom marquise ignores roof window
		}

		handlers.every(h => {
			if (this.calcWidth(h) <= this.marquiseInfo.maxWidth) {
				this.leftHandler = h;
				this.rightHandler = h;
				return false;
			}
		})
	}

	public override calculateMarkers() {
		if (this.type != ElementType.MarquiseTopUp) {
			return;
		}

		this._markers = [];

		if (!this.lineOnFront || !this.rectOnFront) {
			return;
		}

		// if (this.leftHandler.widthCorrection != 0) {
		// 	const _x1 = this.lineOnFront.x1 + (this.leftBar.rectOnFront.w / 2) + this.leftHandler.widthCorrection;
		// 	this._markers.push({ x1: _x1, y1: this.rectOnFront.y, x2: _x1, y2: this.rectOnFront.y + this.rectOnFront.h, w: 0.5 });
		// }

		// if (this.rightHandler.widthCorrection != 0) {
		// 	const _x2 = this.lineOnFront.x3 - (this.leftBar.rectOnFront.w / 2) - this.leftHandler.widthCorrection;
		// 	this._markers.push({ x1: _x2, y1: this.rectOnFront.y, x2: _x2, y2: this.rectOnFront.y + this.rectOnFront.h, w: 0.5 });
		// }

	}

	getDForPath (area) {
		const l = 'lineOn' + area;
		return `M ${this[l].x1} ${this[l].y1}
			L ${this[l].x2} ${this[l].y2}
			L ${this[l].x3} ${this[l].y3}
			L ${this[l].x4} ${this[l].y4}
			Z`;
	}

	public get markers(): ILine[] {
		return this._markers;
	}
}

